import React, { useId, useState } from 'react';
import { Field } from './forms/Field'
import { Input, Select, Textarea } from './forms/Controls'
import styles from './FormField.module.css';

const NON_ALPHANUMERIC_RUN_PATTERN = /[^a-z0-9]+/g;
const EDGE_HYPHEN_PATTERN = /^-|-$/g;

export function FormField({
  label,
  labelExtra,
  name,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  error,
  help,
  compact = false,
  className,
  inputClassName,
  children,
  ...props
}) {
  const generatedId = useId()
  const fallbackName = label
    ? label.toLowerCase().replace(NON_ALPHANUMERIC_RUN_PATTERN, '-').replace(EDGE_HYPHEN_PATTERN, '')
    : generatedId
  const fieldId = `field-${name || fallbackName || 'control'}-${generatedId}`;

  const { options, ...controlProps } = props
  let control = children
  if (!control) {
    if (type === 'textarea') {
      control = <Textarea name={name} value={value} onChange={onChange} placeholder={placeholder} size={compact ? 'compact' : 'default'} className={inputClassName} {...controlProps} />
    } else if (type === 'select') {
      control = (
        <Select name={name} value={value} onChange={onChange} size={compact ? 'compact' : 'default'} className={inputClassName} {...controlProps}>
          {options?.map((option) => <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>)}
        </Select>
      )
    } else {
      control = <Input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} size={compact ? 'compact' : 'default'} className={inputClassName} {...controlProps} />
    }
  }

  return <Field id={fieldId} label={label} labelExtra={labelExtra} required={required} error={error} help={help} className={className}>{control}</Field>
}

export function FormRow({ children }) {
  return <div className={styles.formRow}>{children}</div>;
}

const HEADING_LEVELS = [2, 3, 4, 5, 6]

/**
 * A titled group of fields. `collapsible` turns the heading into a disclosure
 * button (`<hN><button aria-expanded aria-controls>`); the description stays
 * outside the button and describes it. Collapsed content is unmounted, unless
 * `keepMounted`: then it stays mounted but hidden, so work running inside it
 * (an upload, say) is not dropped when the section folds.
 * `headingLevel` (2–6, default 3) sets the heading element, `headingSize` only
 * its visual scale.
 */
export function FormSection({
  title,
  description,
  eyebrow,
  actions,
  children,
  variant = 'minimal',
  collapsible = false,
  keepMounted = false,
  expanded,
  defaultExpanded = true,
  onToggle,
  className,
  contentClassName,
  headingSize = 'section',
  headingLevel = 3,
}) {
  const isControlled = typeof expanded === 'boolean'
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)
  const isExpanded = isControlled ? expanded : internalExpanded
  const resolvedHeadingSize = headingSize === 'subsection' ? 'subsection' : 'section'
  const Heading = `h${HEADING_LEVELS.includes(headingLevel) ? headingLevel : 3}`
  const baseId = useId()
  const contentId = `${baseId}-content`
  const descriptionId = `${baseId}-description`

  const handleToggle = () => {
    const nextValue = !isExpanded
    if (!isControlled) {
      setInternalExpanded(nextValue)
    }
    onToggle?.(nextValue)
  }

  const sectionClasses = [
    styles.formSection,
    variant === 'semantic' ? styles.sectionSemantic : styles.sectionMinimal,
    collapsible ? styles.sectionCollapsible : '',
    className || '',
  ].filter(Boolean).join(' ')

  return (
    <div
      className={sectionClasses}
      data-variant={variant}
      data-heading-size={resolvedHeadingSize}
      data-expanded={collapsible ? String(isExpanded) : undefined}
    >
      {title && (
        collapsible ? (
          <div className={`${styles.sectionHeader} ${styles.sectionHeaderCollapsible}`}>
            <div className={styles.sectionHeading}>
              {eyebrow && <p className={styles.sectionEyebrow}>{eyebrow}</p>}
              <Heading className={styles.sectionTitle}>
                <button
                  type="button"
                  className={styles.sectionToggle}
                  aria-expanded={isExpanded}
                  aria-controls={contentId}
                  aria-describedby={description ? descriptionId : undefined}
                  onClick={handleToggle}
                >
                  <span className={styles.sectionToggleLabel}>{title}</span>
                  <span className={styles.sectionChevron} aria-hidden="true">
                    <svg viewBox="0 0 16 16" fill="none">
                      <path
                        d="M4 6.5 8 10l4-3.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
              </Heading>
              {description && <p id={descriptionId} className={styles.sectionDescription}>{description}</p>}
            </div>
            {actions ? <div className={styles.sectionActions}>{actions}</div> : null}
          </div>
        ) : (
          <div className={styles.sectionHeader}>
            <div className={styles.sectionHeading}>
              {eyebrow && <p className={styles.sectionEyebrow}>{eyebrow}</p>}
              <Heading className={styles.sectionTitle}>{title}</Heading>
              {description && <p className={styles.sectionDescription}>{description}</p>}
            </div>
            {actions ? <div className={styles.sectionActions}>{actions}</div> : null}
          </div>
        )
      )}
      {(!collapsible || isExpanded || keepMounted) && (
        <div id={collapsible ? contentId : undefined} hidden={collapsible && !isExpanded} className={`${styles.sectionContent} ${contentClassName || ''}`.trim()}>{children}</div>
      )}
    </div>
  );
}

export function FormActions({ children, align = 'right', className }) {
  return (
    <div className={`${styles.formActions} ${styles[`align-${align}`]} ${className || ''}`.trim()}>
      {children}
    </div>
  );
}
